export default `# Sweet Pumpkins App Structure

Now, you have your boilerplate set up, you have your API key and you understand how the API works.
Let's build the "Sweet Pumpkins" application!

Here is the mockup of what the application will look like:

![Sweet Pumpkins app mockup](https://sweetpumpkins.codecamps.com/sw-mockup.png)

At the top, we will place the \`Header\` component with a logo.

Below the header, we will split the space into two parts:
- \`Navigation\` components on the left. Her, we will choose the genre, the year of production, the rating and the runtime of the movies
- \`Movies\` component on the right. It will display movies based on the criteria set in the \`Navigation\` area

To style the application, instead of using inline styles, we will create for every component a sibling file with the *.css* extension.
For example, to style the \`Header\` we will add the *Header.css* file and import it into the \`Header\` component.

## Application Skeleton

The entry point to the application is in the *index.js* file. This is where the \`ReactDOM.render\` method mounts the \`App\` component on the \`div\` with \`id=root\`.

Create a \`header\` directory. Inside of the \`header\` directory, create \`Header.js\` and \`Header.css\` files.
We will not implement any logic in the \`Header\` component. That's why it should be a functional component:

\`\`\`plainJavascript
// Header.js

import React from "react";
import "./Header.css";

const Header = () => (
  <header>
    <h1>Sweet Pumpkins</h1>
  </header>
)

export default Header;
\`\`\`

\`\`\`
/* Header.css */

header {
  display: flex;
  height: 80px;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid black;
  background: #334D5C;
}

header h1 {
  font-size: 1.5rem;
}
\`\`\`

Import the \`Header\` component into the \`App\` component by placing the \`import\` statement at the top of *App.js*:

\`\`\`plainJavascript
import Header from "./header/Header";
\`\`\`

Replace the \`h1\` tag in the \`App\` component with the \`<Header />\`. You should also remove the \`console.log\` statement.

The \`App\` component should look like this:

\`\`\`plainJavascript
// App.js

import React from "react";
import Header from "./header/Header";

const App = () => {
  return (
    <div>
      <Header />
    </div>
  );
};

export default App;
\`\`\`

If, at any point, you get lost or your application stops working, you can find a sandbox with the final code for each chapter at the end of that chapter.

Create a new directory *main* and create *navigation* and *movies* directories inside.

Inside the *navigation* directory, create a *Navigation.js* and *Navigation.css* files. For now, it will only display the word "Navigation".

Notice that the \`class\` keyword is a reserved word. That's why, in React, we are using the \`className\` to add a class to an element.

\`\`\`plainJsx
// Navigation.js

import React from "react";
import "./Navigation.css";

class Navigation extends React.Component {
  render() {
    return (
      <section className="navigation">
        Navigation
      </section>
    )
  }
}

export default Navigation;
\`\`\`

Inside of the *movies* directory create a "Movies.js" and "Movies.css" files.
\`\`\`plainJsx
// Movies.js

import React from "react";
import "./Movies.css";

class Movies extends React.Component {
  render() {
    return (
      <section className="movies">
        Movies
      </section>
    )
  }
}

export default Movies;
\`\`\`

Finally, in the *main* directory create *Main.js* and *Main.css* files. Import and display the \`Navigation\` and \`Movies\` components.
\`\`\`plainJsx
// Main.js

import React from "react";
import "./Main.css"
import Navigation from "./navigation/Navigation";
import Movies from "./movies/Movies";

class Main extends React.Component {
  render() {
    return (
      <section className="main">
        <Navigation />
        <Movies />
      </section>
    )
  }
}

export default Main;
\`\`\`

Now you can import the \`Main\` component into the *App.js* and display it.

\`\`\`plainJavascript
// App.js

import React from "react";
import Header from "./header/Header";
import Main from "./main/Main";

const App = () => {
  return (
    <div>
      <Header />
      <Main />
    </div>
  );
};

export default App;
\`\`\`

\`\`\`codeSandbox
n4zy37zzjm
\`\`\`

`;



// WEBPACK FOOTER //
// ./src/book/chapters/app-structure.js